import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  afterNextRender,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../../../../core/auth/auth.service';
import { ProjectService } from '../../services/project.service';
import { TaskService } from '../../services/task.service';
import { TaskStatusService } from '../../services/task-status.service';

export interface QuickTaskProject {
  id: string;
  name: string;
  identifier?: string;
}

/**
 * Contenu de création rapide de tâche affiché DANS un `app-popover` ouvert
 * depuis la sidenav globale. Pas de backdrop/positionnement ici — c'est le
 * popover parent qui s'en charge.
 *
 * Création minimale (même logique que le quick-add kanban,
 * project-detail.component.ts#onKanbanQuickAdd) : le premier statut du
 * projet (ordre sort_order) et le groupe par défaut du projet sont utilisés,
 * puis redirection vers la page d'édition détaillée de la tâche créée.
 */
@Component({
  selector: 'app-quick-task-popover',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="quick-popover">
      <div class="quick-popover-title">Nouvelle tâche</div>

      @if (projects().length > 1) {
        <label class="quick-field">
          <span class="quick-field-label">Projet</span>
          <select
            class="quick-select"
            [value]="selectedProjectId()"
            (change)="onProjectChange($any($event.target).value)"
          >
            @for (project of projects(); track project.id) {
              <option [value]="project.id">{{ project.name }}</option>
            }
          </select>
        </label>
      }

      <label class="quick-field">
        <span class="quick-field-label">Titre</span>
        <input
          #titleInput
          type="text"
          class="quick-input"
          placeholder="Que faut-il faire ?"
          [value]="title()"
          [disabled]="submitting()"
          (input)="title.set($any($event.target).value)"
          (keydown.enter)="onSubmit()"
        />
      </label>

      @if (error()) {
        <div class="quick-error">{{ error() }}</div>
      }

      <div class="quick-popover-footer">
        <button type="button" class="quick-btn quick-btn-ghost" (click)="onCancel()">
          Annuler
        </button>
        <button
          type="button"
          class="quick-btn quick-btn-primary"
          [disabled]="!canSubmit()"
          (click)="onSubmit()"
        >
          {{ submitting() ? 'Création…' : 'Créer la tâche' }}
        </button>
      </div>
    </div>
  `,
  // DA café : titre League Spartan, champs en surface douce, bouton principal
  // café + crème. Tokens uniquement : le sombre suit seul.
  styles: `
    .quick-popover {
      /* max-width borne la largeur quand le panneau parent est contraint
         (mobile : panneau étiré en left/right dans le drawer, plus étroit que 300px) */
      width: min(300px, calc(100vw - 48px));
      max-width: 100%;
      display: flex;
      flex-direction: column;
      gap: 12px;
      padding: 18px;
      box-sizing: border-box;
    }

    .quick-popover-title {
      font-family: var(--font-display);
      font-size: 18px;
      font-weight: 800;
      letter-spacing: -0.02em;
      color: var(--text-primary);
    }

    .quick-field {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    .quick-field-label {
      font-size: 12px;
      font-weight: 700;
      color: var(--text-body);
    }

    .quick-input,
    .quick-select {
      height: 42px;
      box-sizing: border-box;
      width: 100%;
      border: 1px solid var(--border-strong);
      border-radius: var(--radius-field);
      background: var(--surface-soft);
      padding: 0 12px;
      font: inherit;
      font-size: 14px;
      font-weight: 600;
      color: var(--text-primary);
      transition: border-color 0.15s ease, box-shadow 0.15s ease;

      &::placeholder {
        color: var(--text-tertiary);
        font-weight: 500;
      }

      &:focus {
        outline: none;
        border-color: var(--focus-ring-color);
        box-shadow: 0 0 0 3px var(--tint-orange);
      }

      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
    }

    .quick-select {
      cursor: pointer;
    }

    .quick-error {
      font-size: 12px;
      font-weight: 700;
      color: var(--red-600);
    }

    .quick-popover-footer {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 8px;
      padding-top: 4px;
    }

    .quick-btn {
      min-height: 38px;
      padding: 0 16px;
      border-radius: var(--radius-pill);
      border: none;
      font: inherit;
      font-size: 13px;
      font-weight: 700;
      cursor: pointer;
      transition: background 0.15s ease, opacity 0.15s ease;

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      &:focus-visible {
        outline: 2px solid var(--focus-ring-color);
        outline-offset: 2px;
      }
    }

    .quick-btn-ghost {
      background: none;
      color: var(--text-secondary);

      &:hover:not(:disabled) {
        background: var(--surface-sunken);
        color: var(--text-primary);
      }
    }

    .quick-btn-primary {
      background: var(--action-bg);
      color: var(--action-fg);

      &:hover:not(:disabled) {
        background: var(--action-bg-hover);
      }
    }
  `,
})
export class QuickTaskPopoverComponent {
  private router = inject(Router);
  private authService = inject(AuthService);
  private projectService = inject(ProjectService);
  private taskStatusService = inject(TaskStatusService);
  private taskService = inject(TaskService);

  projects = input.required<QuickTaskProject[]>();
  initialProjectId = input<string | null>(null);

  created = output<void>();
  dismissed = output<void>();

  private titleInput = viewChild<ElementRef<HTMLInputElement>>('titleInput');

  title = signal('');
  #selectedProjectId = signal<string | null>(null);
  submitting = signal(false);
  error = signal<string | null>(null);

  selectedProjectId = computed(
    () => this.#selectedProjectId() ?? this.initialProjectId() ?? this.projects()[0]?.id ?? null,
  );

  canSubmit = computed(
    () => this.title().trim().length > 0 && !!this.selectedProjectId() && !this.submitting(),
  );

  constructor() {
    // preventScroll : sans lui, le focus fait défiler horizontalement le rail
    // (overflow hidden reste scrollable par programme) et décale tout le drawer
    afterNextRender(() => this.titleInput()?.nativeElement.focus({ preventScroll: true }));
  }

  onProjectChange(projectId: string): void {
    this.#selectedProjectId.set(projectId);
  }

  onCancel(): void {
    this.dismissed.emit();
  }

  async onSubmit(): Promise<void> {
    if (!this.canSubmit()) return;

    const projectId = this.selectedProjectId();
    const title = this.title().trim();
    const userId = this.authService.profile?.id;

    if (!projectId || !title || !userId) {
      this.error.set('Impossible de créer la tâche : informations manquantes');
      return;
    }

    this.submitting.set(true);
    this.error.set(null);

    try {
      const project = await firstValueFrom(this.projectService.getProjectById(projectId));
      const statuses = await firstValueFrom(
        this.taskStatusService.getStatusesByProject(projectId, { enabledOnly: true }),
      );
      const firstStatus = statuses[0];

      if (!firstStatus) {
        this.error.set('Ce projet n\'a pas encore de statut : ajoutez-en un dans ses paramètres');
        return;
      }

      const task = await firstValueFrom(
        this.taskService.createTask({
          title,
          project_id: projectId,
          organisation_id: project.organisation_id,
          status_id: firstStatus.id,
          group_id: project.default_group_id ?? null,
          created_by: userId,
        }),
      );

      const slug = project.identifier?.toLowerCase() || String(project.id);
      const taskSegment = task.task_number ?? task.id;

      await this.router.navigate(['/project-management', slug, 'task', taskSegment]);

      this.created.emit();
    } catch (err) {
      console.error('Error creating task from quick popover:', err);
      this.error.set('Impossible de créer la tâche : vérifiez votre connexion et réessayez');
    } finally {
      this.submitting.set(false);
    }
  }
}
